import type { Task as FhirTask } from "fhir/r5";

import { oids } from "@/configs/Settings";
import { fhirClients } from "src-next/shared/api/fhirClient";
import { extractResourcesFromBundle } from "../../statistics/helpers/crmMessageFranchiseStatsHelpers";
import { mapFhirTasksToFluxZapBatchTaskViews } from "../helpers/mapFhirTaskToFluxZapBatchTaskView";
import type {
  FluxZapBatchTaskView,
  FluxZapCommunicationRecord,
} from "../types";
import { clinicalCrmFluxZapBasicService } from "./clinicalCrmFluxZapBasicService";

export interface ClinicalCrmCommunicationsData {
  communications: FluxZapCommunicationRecord[];
  batchTasks: FluxZapBatchTaskView[];
}

export const CLINICAL_CRM_COMMUNICATIONS_EMPTY_DATA: ClinicalCrmCommunicationsData =
  {
    communications: [],
    batchTasks: [],
  };

/** Mesmo padrão de `crmMessageFranchiseStatsService` para owner da Task. */
function buildOrganizationOwnerIdForTaskSearch(organizationId: string): string {
  return `${oids.cnpj}-${organizationId.trim()}`;
}

async function fetchBatchNotificationTasks(
  organizationId: string,
): Promise<FhirTask[]> {
  const ownerId = buildOrganizationOwnerIdForTaskSearch(organizationId);
  try {
    const bundle = await fhirClients.workflow.search<FhirTask>("Task", {
      owner: ownerId,
      _sort: "-authored-on",
      _count: "1000",
    });
    return extractResourcesFromBundle<FhirTask>(bundle);
  } catch (err) {
    console.warn("clinicalCrmCommunicationsService.fetchTasks", err);
    return [];
  }
}

/**
 * Carrega comunicações (Basic) e histórico de disparos (Task), cruzando destinatários.
 */
async function fetchClinicalCrmCommunicationsData(
  organizationId: string,
): Promise<ClinicalCrmCommunicationsData> {
  const trimmed = organizationId?.trim();
  if (!trimmed) {
    return { ...CLINICAL_CRM_COMMUNICATIONS_EMPTY_DATA };
  }

  const [communicationsResult, tasksResult] = await Promise.allSettled([
    clinicalCrmFluxZapBasicService.fetchFluxZapCommunicationsByOrganization(
      trimmed,
    ),
    fetchBatchNotificationTasks(trimmed),
  ]);

  const communications =
    communicationsResult.status === "fulfilled"
      ? communicationsResult.value
      : [];
  const tasks =
    tasksResult.status === "fulfilled" ? tasksResult.value : [];

  const batchTasks = mapFhirTasksToFluxZapBatchTaskViews(tasks, communications);

  return { communications, batchTasks };
}

export const clinicalCrmCommunicationsService = {
  fetchClinicalCrmCommunicationsData,
};
